import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Box,
  Tooltip,
  Input,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { validateFile, getAcceptAttribute, getAllowedFileTypesDisplay } from '../../utils/fileValidation';
import { useFormValidation } from '../../hooks/useInputValidation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import { projectService } from '../../services/projectService';
import { artifactService } from '../../services/artifactService';
import { Project, ProjectCreate } from '../../types/api.types';

const SidebarContainer = styled('div')(({ theme }) => ({
  width: '280px',
  height: '100%',
  borderRight: `1px solid #e0e0e0`,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#ffffff',
}));

const SidebarHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid #e0e0e0`,
  backgroundColor: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const ProjectList = styled(List)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(1, 0),
  backgroundColor: '#ffffff',
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  margin: theme.spacing(0, 1),
  borderRadius: 0,
  backgroundColor: 'transparent',
  marginBottom: 0,
  borderBottom: '1px solid #f0f0f0',
  '&:hover': {
    backgroundColor: '#f8f9fa',
  },
}));

const NewProjectButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  padding: theme.spacing(0.75, 2),
  borderRadius: '20px',
  background: '#11CAF0',
  color: '#ffffff',
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: '#0097a7',
  },
  width: '139px',
  height: '37px',
  flexShrink: 0
}));

const LoadingContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const UploadButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  padding: theme.spacing(1, 2),
  borderRadius: '8px',
  border: '2px dashed #cccccc',
  backgroundColor: 'transparent',
  color: '#666666',
  minWidth: 'auto',
  width: '100%',
  '&:hover': {
    backgroundColor: 'rgba(17, 202, 240, 0.1)',
    borderColor: '#11CAF0',
    color: '#11CAF0',
  },
  '&.uploaded': {
    borderColor: '#4caf50',
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    color: '#4caf50',
  },
}));

const HiddenInput = styled('input')({
  display: 'none',
});

interface ProjectSidebarProps {
  selectedProject: string | null;
  setSelectedProject: (id: string | null) => void;
  refreshTrigger?: number; // Optional prop to force refresh
}

const ProjectSidebar: React.FC<ProjectSidebarProps> = ({
  selectedProject,
  setSelectedProject,
  refreshTrigger,
}) => {
  // State for projects
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for project dialog
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Input validation for form fields
  const formValidation = useFormValidation({
    projectName: {
      validationType: 'projectName',
      initialValue: '',
    },
    projectDescription: {
      validationType: 'projectDescription',
      initialValue: '',
    },
    burpHost: {
      validationType: 'burpSuiteHost',
      initialValue: '',
    },
  });

  // Burp Suite API Key doesn't need validation (it's a secure token)
  const [burpApiKey, setBurpApiKey] = useState('');


  // State for project scope file upload
  const [projectScopeFile, setProjectScopeFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  // State for Burp Suite configuration
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);
  
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch projects on component mount and when refreshTrigger changes
  useEffect(() => {
    console.log('ProjectSidebar: Component mounted or refresh triggered, fetching projects...');
    fetchProjects();
  }, [refreshTrigger]);

  // Fetch projects from API
  const fetchProjects = async () => {
    try {
      setLoading(true);
      console.log('ProjectSidebar: Fetching projects...');
      const fetchedProjects = await projectService.getProjects();
      console.log('ProjectSidebar: Fetched projects:', fetchedProjects);
      console.log('ProjectSidebar: Number of projects:', fetchedProjects.length);
      setProjects(fetchedProjects);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError('Failed to load projects. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Dialog handlers
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    formValidation.resetAll();
    setBurpApiKey('');
    setProjectScopeFile(null);
    setSubmitting(false);
    setUploadingFile(false);
    setShowAdvancedConfig(false);
    setError(null);
  };

  // File upload handlers
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate the file
    const validationResult = validateFile(file);
    
    if (!validationResult.valid) {
      // Show error message
      setError(validationResult.error || 'Invalid file');
      // Clear the file input
      event.target.value = '';
      return;
    }

    // File is valid, set it
    setProjectScopeFile(file);
    setError(null);
  };

  const handleFileUpload = () => {
    const fileInput = document.getElementById('project-scope-file-input') as HTMLInputElement;
    fileInput?.click();
  };

  const removeFile = () => {
    setProjectScopeFile(null);
    const fileInput = document.getElementById('project-scope-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Add project handler
  const handleAddProject = async () => {
    // Validate all fields before submission
    if (!formValidation.validateAll()) {
      setError('Please fix validation errors before submitting. Ensure Project Name is at least 1 character, no HTML tags or special characters like < > are allowed.');
      return;
    }

    const sanitizedValues = formValidation.getSanitizedValues();
    
    if (sanitizedValues.projectName.trim()) {
      try {
        setSubmitting(true);
        
        const newProject: ProjectCreate = {
          name: sanitizedValues.projectName.trim(),
          description: sanitizedValues.projectDescription.trim() || undefined,
          target_scope: [],
          status: 'active',
          ...(sanitizedValues.burpHost.trim() && { burp_host: sanitizedValues.burpHost.trim() }),
          ...(burpApiKey.trim() && { burp_api_key: burpApiKey.trim() }),
        };
        
        const createdProject = await projectService.createProject(newProject);
        console.log('ProjectSidebar: Created project response:', createdProject);
        console.log('ProjectSidebar: Project ID field (_id):', createdProject._id);
        console.log('ProjectSidebar: Project keys:', Object.keys(createdProject));
        console.log('ProjectSidebar: Current projects state before update:', projects);
        
        // If there's a project scope file, upload it as an artifact
        if (projectScopeFile && createdProject._id) {
          try {
            setUploadingFile(true);
            
            // Create an artifact for the project scope
            const artifact = await artifactService.createArtifact({
              name: 'Project Scope Document',
              type: 'scope',
              project_id: createdProject._id,
              content: null,
              is_manual: true
            });
            
            // Upload the file to the artifact
            await artifactService.uploadArtifactFile(
              artifact.id,
              projectScopeFile,
              createdProject._id
            );
            
            console.log('Project scope file uploaded successfully');
          } catch (fileError) {
            console.error('Failed to upload project scope file:', fileError);
            setError('Project created successfully, but failed to upload scope file. You can upload it later.');
          } finally {
            setUploadingFile(false);
          }
        }
        
        // Add the new project to the state immediately for better UX
        setProjects(prevProjects => {
          const updatedProjects = [...prevProjects, createdProject];
          console.log('ProjectSidebar: Updated projects state:', updatedProjects);
          return updatedProjects;
        });
        
        // Also refresh the projects list to ensure persistence with backend
        console.log('ProjectSidebar: Calling fetchProjects to sync with backend...');
        await fetchProjects();
        
        // Select the new project
        if (createdProject._id) {
          setSelectedProject(createdProject._id);
        }
        
        // Close dialog
        handleClose();
      } catch (err) {
        console.error('Failed to create project:', err);
        setError(err instanceof Error ? err.message : 'Failed to create project. Please try again.');
      } finally {
        setSubmitting(false);
        setUploadingFile(false);
      }
    }
  };

  // Clear error message
  const handleCloseError = () => {
    setError(null);
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (projectId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent selecting the project
    setProjectToDelete(projectId);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  // Delete project handler
  const handleDeleteProject = async () => {
    if (projectToDelete) {
      try {
        setDeleting(true);
        await projectService.deleteProject(projectToDelete);
        
        // Remove from local state
        setProjects(projects.filter(p => p._id !== projectToDelete));
        
        // If the deleted project was selected, select another one
        if (selectedProject === projectToDelete) {
          const nextProject = projects.find(p => p._id !== projectToDelete);
          if (nextProject) {
            setSelectedProject(nextProject._id);
          } else {
            // No projects left
            setSelectedProject('');
          }
        }
        
        // Close dialog
        handleCloseDeleteDialog();
      } catch (err) {
        console.error('Failed to delete project:', err);
        setError('Failed to delete project. Please try again.');
      } finally {
        setDeleting(false);
      }
    }
  };

  return (
    <SidebarContainer>
      <SidebarHeader>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333333' }}>
          Projects
        </Typography>
        <NewProjectButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpen}
        >
          New Project
        </NewProjectButton>
      </SidebarHeader>
      
      <ProjectList>
        {loading ? (
          <LoadingContainer>
            <CircularProgress size={24} />
          </LoadingContainer>
        ) : projects.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            No projects yet. Create a new project to get started.
          </Typography>
        ) : (
          projects.map((project) => (
            <StyledListItem 
              key={project._id} 
              disablePadding
              secondaryAction={
                <Tooltip title="Delete project">
                  <IconButton 
                    edge="end" 
                    aria-label="delete" 
                    onClick={(e) => handleOpenDeleteDialog(project._id, e)}
                    size="small"
                    sx={{ color: '#6c757d' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemButton 
                selected={selectedProject === project._id}
                onClick={() => setSelectedProject(project._id)}
                sx={{
                  borderRadius: 0,
                  '&.Mui-selected': {
                    backgroundColor: '#e8f5e8',
                    '&:hover': {
                      backgroundColor: '#e8f5e8',
                    },
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#333333' }}>
                    {project.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888888', mt: 0.25 }}>
                    {new Date(project.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </ListItemButton>
            </StyledListItem>
          ))
        )}
      </ProjectList>

      {/* Project Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF', // White background
            color: '#000000', // Black text
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          Create New Project
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          <TextField
            margin="dense"
            id="name"
            label="Project Name"
            type="text"
            fullWidth
            variant="outlined"
            disabled={submitting}
            {...formValidation.fields.projectName.getInputProps()}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                color: '#000000',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#000000',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(0, 0, 0, 0.7)',
                '&.Mui-focused': {
                  color: '#000000',
                },
              },
            }}
          />
          <TextField
            margin="dense"
            id="description"
            label="Project Description (optional)"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            disabled={submitting}
            {...formValidation.fields.projectDescription.getInputProps()}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                color: '#000000',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#000000',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(0, 0, 0, 0.7)',
                '&.Mui-focused': {
                  color: '#000000',
                },
              },
            }}
          />
          
          {/* Project Scope File Upload */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: '#000000', fontWeight: 500 }}>
              Project Scope Document (optional)
            </Typography>
            <Typography variant="caption" sx={{ mb: 1, color: 'rgba(0, 0, 0, 0.6)', display: 'block' }}>
              Supported formats: {getAllowedFileTypesDisplay()}
            </Typography>
            <HiddenInput
              id="project-scope-file-input"
              type="file"
              accept={getAcceptAttribute()}
              onChange={handleFileChange}
              disabled={submitting || uploadingFile}
            />
            
            {!projectScopeFile ? (
              <UploadButton
                onClick={handleFileUpload}
                disabled={submitting || uploadingFile}
                startIcon={<UploadFileIcon />}
              >
                Upload Project Scope
              </UploadButton>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1,
                border: '2px solid #4caf50',
                borderRadius: '8px',
                backgroundColor: 'rgba(76, 175, 80, 0.1)'
              }}>
                <AttachFileIcon sx={{ color: '#4caf50' }} />
                <Typography variant="body2" sx={{ color: '#4caf50', flexGrow: 1 }}>
                  {projectScopeFile.name}
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={removeFile}
                  disabled={submitting || uploadingFile}
                  sx={{ color: '#4caf50' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            
            <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', mt: 0.5, display: 'block' }}>
              Supported formats: PDF, DOC, DOCX, TXT, MD, JSON, XML, CSV
            </Typography>
          </Box>

          {/* Burp Suite Configuration */}
          <Box sx={{ mt: 3 }}>
            <Accordion
              expanded={showAdvancedConfig}
              onChange={() => setShowAdvancedConfig(!showAdvancedConfig)}
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                '&:before': { display: 'none' },
                boxShadow: 'none',
                border: '1px solid rgba(0, 0, 0, 0.1)'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 48,
                  '&.Mui-expanded': { minHeight: 48 },
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    '&.Mui-expanded': { margin: '12px 0' }
                  }
                }}
              >
                <SettingsIcon sx={{ mr: 1, color: 'rgba(0, 0, 0, 0.6)' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                  Advanced Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.7)', mb: 2, display: 'block' }}>
                  Configure project-specific settings for external tools. Leave blank to use system defaults.
                </Typography>

                <Typography variant="body2" sx={{ mb: 1, color: '#000000', fontWeight: 500 }}>
                  Burp Suite Configuration
                </Typography>

                <TextField
                  margin="dense"
                  label="Burp Suite Host/IP (optional)"
                  placeholder="e.g. localhost:1337 or 192.168.1.100:1337"
                  type="text"
                  fullWidth
                  variant="outlined"
                  {...formValidation.fields.burpHost.getInputProps()}
                  disabled={submitting}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      color: '#000000',
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#000000',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                      '&.Mui-focused': {
                        color: '#000000',
                      },
                    },
                  }}
                />

                <TextField
                  margin="dense"
                  label="Burp Suite API Key (optional)"
                  type="password"
                  fullWidth
                  variant="outlined"
                  value={burpApiKey}
                  onChange={(e) => setBurpApiKey(e.target.value)}
                  disabled={submitting}
                  sx={{
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      color: '#000000',
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#000000',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                      '&.Mui-focused': {
                        color: '#000000',
                      },
                    },
                  }}
                />

                <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block' }}>
                  Specify custom Burp Suite connection details for this project. If not provided, system defaults will be used.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          <Button 
            onClick={handleClose} 
            disabled={submitting}
            sx={{ color: '#000000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddProject} 
            variant="contained" 
            disabled={!formValidation.fields.projectName.value.trim() || submitting || uploadingFile}
            startIcon={(submitting || uploadingFile) ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : null}
            sx={{
              backgroundColor: '#000000',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#333333',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                color: 'rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            {submitting ? 'Creating...' : uploadingFile ? 'Uploading...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseError}>
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF', // White background
            color: '#000000', // Black text
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          Delete Project
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          <Typography sx={{ color: '#000000' }}>
            Are you sure you want to delete this project? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#FFFFFF', color: '#000000' }}>
          <Button 
            onClick={handleCloseDeleteDialog} 
            disabled={deleting}
            sx={{ color: '#000000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteProject} 
            variant="contained" 
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} sx={{ color: '#ffffff' }} /> : null}
            sx={{
              backgroundColor: '#FF4444',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#FF6666',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                color: 'rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarContainer>
  );
};

export default ProjectSidebar;